import { useMemo, useState } from 'react';
import { UserGroupMultiSelect } from '../../components/UserGroupMultiSelect';
import type { AcknowledgementAudience } from './types';

interface PutInForceAudienceModalProps {
  open: boolean;
  versionLabel: string;
  roleOptions: string[];
  onCancel: () => void;
  onConfirm: (audience: AcknowledgementAudience | null) => Promise<void>;
}

export function PutInForceAudienceModal({
  open,
  versionLabel,
  roleOptions,
  onCancel,
  onConfirm,
}: PutInForceAudienceModalProps) {
  const [allUsers, setAllUsers] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [userIdsRaw, setUserIdsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedUserIds = useMemo(() => {
    const ids = userIdsRaw
      .split(/[\s,;]+/)
      .map((part) => Number.parseInt(part.trim(), 10))
      .filter((id) => Number.isInteger(id) && id > 0);
    return [...new Set(ids)].slice(0, 500);
  }, [userIdsRaw]);

  if (!open) return null;

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role].slice(0, 50),
    );
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const audience: AcknowledgementAudience | null = allUsers
        ? null
        : {
            targetRoles: selectedRoles,
            targetGroupIds: selectedGroupIds,
            targetUserIds: parsedUserIds,
          };
      await onConfirm(audience);
    } catch (e) {
      setError((e as Error).message ?? 'No se pudo publicar la versión');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-lg rounded-md border bg-card p-4 shadow-lg space-y-4"
        role="dialog"
        aria-labelledby="put-in-force-title"
      >
        <div>
          <h2 id="put-in-force-title" className="text-lg font-semibold">
            Publicar versión {versionLabel}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Definí quién debe confirmar la lectura del documento al quedar vigente.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allUsers}
            onChange={(event) => setAllUsers(event.target.checked)}
          />
          Todos los usuarios de la organización
        </label>

        {!allUsers && (
          <div className="space-y-3 border rounded-md p-3 bg-muted/20">
            <div>
              <p className="text-sm font-medium mb-2">Roles</p>
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                {roleOptions.map((role) => (
                  <label key={role} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                    />
                    {role}
                  </label>
                ))}
                {roleOptions.length === 0 && (
                  <span className="text-xs text-muted-foreground">Sin roles cargados</span>
                )}
              </div>
            </div>

            <UserGroupMultiSelect
              label="Grupos (GRP)"
              selectedIds={selectedGroupIds}
              onChange={setSelectedGroupIds}
            />

            <div>
              <label className="text-sm font-medium" htmlFor="ack-user-ids">
                Usuarios (IDs separados por coma)
              </label>
              <input
                id="ack-user-ids"
                className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                placeholder="ej. 12, 34, 56"
                value={userIdsRaw}
                onChange={(event) => setUserIdsRaw(event.target.value.slice(0, 2000))}
              />
            </div>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting ? 'Publicando…' : 'Publicar y notificar'}
          </button>
        </div>
      </div>
    </div>
  );
}
